import { create } from 'zustand'
import { useEffect } from 'react'

export type ToastKind = 'info' | 'success' | 'error'

export type Toast = {
  id: number
  kind: ToastKind
  message: string
}

type ToastState = {
  toasts: Toast[]
  push: (t: Omit<Toast, 'id'>) => number
  dismiss: (id: number) => void
}

let _id = 0

export const useToastStore = create<ToastState>((set) => ({
  toasts: [],
  push(t) {
    const id = ++_id
    set((s) => ({ toasts: [...s.toasts, { id, ...t }] }))
    return id
  },
  dismiss(id) {
    set((s) => ({ toasts: s.toasts.filter((t) => t.id !== id) }))
  },
}))

export function toast(message: string, kind: ToastKind = 'info') {
  return useToastStore.getState().push({ message, kind })
}

export const toastSuccess = (m: string) => toast(m, 'success')
export const toastError = (m: string) => toast(m, 'error')
export const toastInfo = (m: string) => toast(m, 'info')

export function ToastHost() {
  const { toasts, dismiss } = useToastStore()

  useEffect(() => {
    if (!toasts.length) return
    const timers = toasts.map((t) =>
      setTimeout(() => dismiss(t.id), t.kind === 'error' ? 6000 : 3500),
    )
    return () => timers.forEach(clearTimeout)
  }, [toasts, dismiss])

  return (
    <div className="fixed top-[max(1rem,env(safe-area-inset-top))] right-[max(1rem,env(safe-area-inset-right))] z-50 flex flex-col gap-2 no-print" role="status" aria-live="polite">
      {toasts.map((t) => (
        <div
          key={t.id}
          className={
            'rounded-lg shadow-lg px-4 py-3 min-w-[240px] text-white font-medium cursor-pointer ' +
            (t.kind === 'error'
              ? 'bg-rose-600'
              : t.kind === 'success'
                ? 'bg-emerald-600'
                : 'bg-slate-800')
          }
          onClick={() => dismiss(t.id)}
        >
          {t.message}
        </div>
      ))}
    </div>
  )
}
